import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner,
  faClock,
  faMapMarkerAlt,
  faExclamationTriangle,
  faUndoAlt
} from '@fortawesome/free-solid-svg-icons';
import { ApplicationStatus, getApplicationStatus, withdrawApplication } from '../../services/applicationService';
import { logger } from '../../utils/logger';
import StatusTracker from '../application/StatusTracker';
import ApplicationSummary from '../application/ApplicationSummary';
import ConfirmDialog from '../common/ConfirmDialog';
import PageHeader from '../common/PageHeader';
import { useAnalytics, useScrollTracking } from '../../hooks/useAnalytics';
import '../../styles/EventApplication.css';

const EventApplication = (): React.ReactElement => {
  const { id: applicationId } = useParams<{ id: string }>();
  const [application, setApplication] = useState<ApplicationStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [qrImageError, setQrImageError] = useState<boolean>(false);  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showWithdrawDialog, setShowWithdrawDialog] = useState<boolean>(false);
  const [withdrawLoading, setWithdrawLoading] = useState<boolean>(false);
  const contentSectionRef = useRef<HTMLElement | null>(null);
  const { trackPageView, trackCustomEvent } = useAnalytics();
  useScrollTracking(); // Automatic scroll tracking
  
  // Function to check if elements are in viewport to apply fade effect - memoized with useCallback
  const checkFade = useCallback(() => {
    if (contentSectionRef.current) {
      const elementTop = contentSectionRef.current.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;
      
      // More generous threshold for activation - if element is in viewport or close to it
      if (elementTop < windowHeight - 50) {
        contentSectionRef.current.classList.add('active');
      }
    }
  }, []);

  // Memoize the fetchApplicationStatus function with useCallback
  const fetchApplicationStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    if (!applicationId) {
      logger.error('❌ No application ID provided');
      setError('Application ID is missing. Please check the URL and try again.');
      setLoading(false);
      return;
    }
    
    try {
      const data = await getApplicationStatus(applicationId);
      setApplication(data);
      
      // Track page view with application data
      trackPageView({
        page_title: 'Application Status',
        page_location: window.location.href
      });
      
      // Track application status view
      trackCustomEvent('application_status_viewed', {
        application_id: data.id,
        status: data.status,
        days_since_submit: Math.floor((Date.now() - new Date(data.submittedAt).getTime()) / (1000 * 60 * 60 * 24))
      });
      
      logger.info('📄 Application data loaded', { id: data.id, status: data.status });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load application status: ${errorMessage}`);
      
      // Track error event
      trackCustomEvent('application_view_error', {
        error_message: errorMessage,
        application_id: applicationId
      });
      
      logger.error('❌ Failed to load application', { id: applicationId, error: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [applicationId, trackPageView, trackCustomEvent]);
  
  // Memoize the handleWithdrawApplication function with useCallback
  const handleWithdrawApplication = useCallback(async () => {
    if (!application || withdrawLoading) return;
    
    setWithdrawLoading(true);
    try {      await withdrawApplication(application.id);
      
      // Track withdraw success
      trackCustomEvent('application_withdrawn', {
        application_id: application.id,
        status_before_withdrawal: application.status,
        event_id: application.eventDetails?.address || 'unknown' // Using only address for location consistency
      });
      
      // Refetch application to get updated status
      await fetchApplicationStatus();
      setShowWithdrawDialog(false);
      logger.success('✅ Application withdrawn successfully', { id: application.id });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      
      // Track withdraw error
      trackCustomEvent('application_withdraw_error', {
        application_id: application.id,
        error_message: errorMessage
      });
      
      logger.error('❌ Failed to withdraw application', { id: application.id, error: errorMessage });
      alert(`Failed to withdraw application: ${errorMessage}`);
    } finally {
      setWithdrawLoading(false);
    }
  }, [application, withdrawLoading, fetchApplicationStatus, trackCustomEvent]);
    // QR code viewed tracking
  const handleQrCodeViewed = useCallback(() => {
    if (application && application.status === 'approved') {
      trackCustomEvent('qr_code_viewed', {
        application_id: application.id,
        status: application.status
      });
    }
  }, [application, trackCustomEvent]);
  
  // Set up fade effect on scroll and fetch application data
  useEffect(() => {
    // Initial check for fade effect
    const initialCheck = () => {
      checkFade();
    };
    
    // Call immediately and after a short delay to ensure DOM is ready
    initialCheck();
    const timer = setTimeout(initialCheck, 100);
    
    window.addEventListener('scroll', checkFade);
    fetchApplicationStatus();
    
    return () => {
      window.removeEventListener('scroll', checkFade);
      clearTimeout(timer);
    };
  }, [checkFade, fetchApplicationStatus]);
  
  // Activate fade-in when application data is loaded
  useEffect(() => {
    if (application && contentSectionRef.current) {
      // Ensure the content becomes visible when data is loaded
      contentSectionRef.current.classList.add('active');
    }
  }, [application]);

  // Effect to handle QR code image load errors
  useEffect(() => {
    if (application?.qrCodeUrl) {
      setQrImageError(false);
    }
  }, [application?.qrCodeUrl]);
  // Effect to track QR code viewing when the component first renders
  useEffect(() => {
    if (application?.status === 'approved' && application?.qrCodeUrl) {
      // Track QR view after a small delay to ensure rendering
      const timerId = setTimeout(() => {
        handleQrCodeViewed();
      }, 1500); // 1.5 second delay to ensure user has seen it

      return () => clearTimeout(timerId); // Cleanup timeout if component unmounts
    }
  }, [application, handleQrCodeViewed]);

  // Render the application status page based on loading/error/data state
  return (
    <>
      <PageHeader
        title="Application Status"
      />
      
      {loading && (
        <div className="evt-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
          <p>Loading application status...</p>
        </div>
      )}
      
      {error && (
        <div className="evt-error">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      )}
      
      {!loading && !error && application && (
        <section className="container fade-in" ref={contentSectionRef}>          
        <div className="evt-header">
          <h2 className="evt-event-title">{(application.eventDetails as any)?.title || 'Event Application'}</h2>            <div className="evt-meta">
              {application.eventDetails && (
                <>
                  <div className="evt-meta-item">
                    <FontAwesomeIcon icon={faClock} />
                    <span>Event Date: {new Date(application.eventDetails.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>                  <div className="evt-meta-item">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    <span>Location: {application.eventDetails.address || 'TBA'}</span>
                  </div>
                </>
              )}
            </div>
          </div>          {/* Display QR Code directly when application is approved */}
          {application.qrCodeUrl && application.status === 'approved' && (
            <div className="evt-inline-qr-container">
              <div className="evt-qr-content">
                <p>Present this QR code at the entrance for quick check-in.</p>
                {qrImageError ? (
                  <div className="evt-qr-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                    <p>Failed to load QR code image.</p>
                  </div>
                ) : (
                  <img 
                    src={application.qrCodeUrl} 
                    alt="Entry QR Code" 
                    className="evt-qr-image" 
                    onError={() => setQrImageError(true)} 
                    onLoad={() => handleQrCodeViewed()} 
                  />
                )}
                <p className="evt-qr-info">Application ID: {application.id}</p>
              </div>
            </div>
          )}
            <StatusTracker status={application.status} />
          
          <ApplicationSummary application={application} />            <div className="evt-actions">
            {/* Only show withdraw button for pending or approved applications that are not checked in */}
            {(application.status === 'pending' || application.status === 'approved') && 
              application.checkInStatus !== 'checked_in' && (
              <button 
                className="evt-btn evt-secondary" 
                onClick={() => setShowWithdrawDialog(true)}
                disabled={withdrawLoading}
              >
                {withdrawLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUndoAlt} /> Withdraw Application
                  </>
                )}
              </button>
            )}
            {/* Show checked-in confirmation when user has checked in */}
            {application.checkInStatus === 'checked_in' && (
              <div className="evt-checked-in">
                <span className="evt-checked-in-icon">✓</span>
                <span className="evt-checked-in-text">Checked In</span>
                {application.checkInTime && (
                  <span className="evt-checked-in-time">
                    {new Date(application.checkInTime).toLocaleString()}
                  </span>
                )}
              </div>
            )}
          </div>
            {/* QR code now displayed directly in the page */}
          
          {/* Withdraw Confirmation Dialog */}
          {showWithdrawDialog && (
            <ConfirmDialog 
              title="Withdraw Application"
              message="Are you sure you want to withdraw your application? This action cannot be undone."
              confirmLabel="Yes, withdraw"
              cancelLabel="Cancel"
              onConfirm={handleWithdrawApplication}
              onCancel={() => setShowWithdrawDialog(false)}
              isLoading={withdrawLoading}
              isDestructive={true}
            />
          )}
        </section>
      )}
    </>
  );
};

export default EventApplication;
